/**
 * 样式工具函数
 * 提供样式组合、响应式、主题相关的工具函数
 */

import { SxProps, Theme } from "@mui/material/styles";
import {
  colors,
  spacing,
  borderRadius,
  shadows,
  typography,
  breakpoints,
} from "./variables";

// 样式组合工具函数
export const composeStyles = (...styles: SxProps<Theme>[]): SxProps<Theme> => {
  return styles.reduce((acc, style) => {
    return { ...acc, ...style };
  }, {});
};

// 样式组合工具函数（兼容旧版本）
export const getButtonStyles = (styles: string[]): SxProps<Theme> => {
  // 返回空样式对象，实际样式将在组件中定义
  return {};
};

// 统一按钮样式组合函数（兼容旧版本）
export const getStandardButtonStyles = (
  options: {
    variant?: "contained" | "outlined" | "text";
    color?: "primary" | "secondary" | "error" | "success";
    size?: "small" | "medium" | "large";
    type?: "default" | "dialog" | "fullWidth";
  } = {}
): SxProps<Theme> => {
  const {
    variant = "contained",
    color = "primary",
    size = "small",
    type = "default",
  } = options;

  // 直接返回基于选项的样式，不依赖外部样式对象
  const baseStyles: SxProps<Theme> = {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    },
  };

  // 根据变体添加样式
  if (variant === "contained") {
    Object.assign(baseStyles, {
      backgroundColor: (theme: Theme) => theme.palette[color].main,
      color: (theme: Theme) => theme.palette[color].contrastText,
      "&:hover": {
        ...(baseStyles["&:hover"] as object),
        backgroundColor: (theme: Theme) => theme.palette[color].dark,
      },
    });
  } else if (variant === "outlined") {
    Object.assign(baseStyles, {
      border: "2px solid",
      borderColor: (theme: Theme) => theme.palette[color].main,
      color: (theme: Theme) => theme.palette[color].main,
      backgroundColor: "transparent",
      "&:hover": {
        ...(baseStyles["&:hover"] as object),
        backgroundColor: (theme: Theme) => theme.palette[color].main,
        color: (theme: Theme) => theme.palette[color].contrastText,
      },
    });
  } else if (variant === "text") {
    Object.assign(baseStyles, {
      color: (theme: Theme) => theme.palette[color].main,
      backgroundColor: "transparent",
      "&:hover": {
        ...(baseStyles["&:hover"] as object),
        backgroundColor: (theme: Theme) => theme.palette[color].light,
        color: (theme: Theme) => theme.palette[color].dark,
      },
    });
  }

  // 根据尺寸添加样式
  if (size === "small") {
    Object.assign(baseStyles, {
      fontSize: "0.875rem",
      padding: "6px 16px",
    });
  } else if (size === "medium") {
    Object.assign(baseStyles, {
      fontSize: "1rem",
      padding: "8px 22px",
    });
  } else if (size === "large") {
    Object.assign(baseStyles, {
      fontSize: "1.125rem",
      padding: "11px 28px",
    });
  }

  // 根据类型添加样式
  if (type === "dialog") {
    Object.assign(baseStyles, {
      minWidth: "80px",
    });
  } else if (type === "fullWidth") {
    Object.assign(baseStyles, {
      width: "100%",
    });
  }

  return baseStyles;
};

// 输入框样式组合工具函数（兼容旧版本）
export const getTextFieldStyles = (styles: string[]): SxProps<Theme> => {
  // 返回空样式对象，实际样式将在组件中定义
  return {};
};

// 统一输入框样式组合函数（兼容旧版本）
export const getStandardTextFieldStyles = (
  options: {
    type?: "default" | "dialog" | "error";
    variant?: "standard" | "outlined" | "filled";
  } = {}
): SxProps<Theme> => {
  const { type = "default", variant = "outlined" } = options;

  // 直接返回基于选项的样式
  const baseStyles: SxProps<Theme> = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "0px",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.23)",
        borderWidth: "2px",
      },
      "&:hover fieldset": {
        borderColor: (theme: Theme) => theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: (theme: Theme) => theme.palette.primary.main,
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(0, 0, 0, 0.6)",
      "&.Mui-focused": {
        color: (theme: Theme) => theme.palette.primary.main,
      },
    },
    "& .MuiInputBase-input": {
      color: "rgba(0, 0, 0, 0.87)",
    },
  };

  // 根据类型添加样式
  if (type === "dialog") {
    Object.assign(baseStyles, {
      "& .MuiOutlinedInput-root": {
        ...(baseStyles["& .MuiOutlinedInput-root"] as object),
        minWidth: "200px",
      },
    });
  } else if (type === "error") {
    Object.assign(baseStyles, {
      "& .MuiOutlinedInput-root": {
        ...(baseStyles["& .MuiOutlinedInput-root"] as object),
        "& fieldset": {
          borderColor: (theme: Theme) => theme.palette.error.main,
        },
        "&:hover fieldset": {
          borderColor: (theme: Theme) => theme.palette.error.main,
        },
        "&.Mui-focused fieldset": {
          borderColor: (theme: Theme) => theme.palette.error.main,
        },
      },
      "& .MuiInputLabel-root": {
        ...(baseStyles["& .MuiInputLabel-root"] as object),
        color: (theme: Theme) => theme.palette.error.main,
        "&.Mui-focused": {
          color: (theme: Theme) => theme.palette.error.main,
        },
      },
    });
  }

  return baseStyles;
};

// 条件样式函数
export const conditionalStyle = (
  condition: boolean,
  trueStyle: SxProps<Theme>,
  falseStyle: SxProps<Theme> = {}
): SxProps<Theme> => {
  return condition ? trueStyle : falseStyle;
};

// 响应式样式工具函数
export const responsive = {
  // 断点工具
  breakpoint: (
    breakpoint: keyof typeof breakpoints,
    styles: SxProps<Theme>
  ) => ({
    [`@media (min-width: ${breakpoints[breakpoint]}px)`]: styles,
  }),

  // 移动端优先
  mobileFirst: (styles: Record<string, SxProps<Theme>>) => {
    const result: SxProps<Theme> = {};

    Object.entries(styles).forEach(([bp, style]) => {
      if (bp === "xs") {
        Object.assign(result, style);
      } else {
        Object.assign(result, {
          [`@media (min-width: ${
            breakpoints[bp as keyof typeof breakpoints]
          }px)`]: style,
        });
      }
    });

    return result;
  },

  // 桌面端优先
  desktopFirst: (styles: Record<string, SxProps<Theme>>) => {
    const result: SxProps<Theme> = {};

    Object.entries(styles).forEach(([bp, style]) => {
      if (bp === "xl") {
        Object.assign(result, style);
      } else {
        Object.assign(result, {
          [`@media (max-width: ${
            breakpoints[bp as keyof typeof breakpoints] - 1
          }px)`]: style,
        });
      }
    });

    return result;
  },
};

// 主题工具函数
export const themeUtils = {
  // 获取主题颜色
  color: (colorPath: string) => (theme: Theme) => {
    const path = colorPath.split(".");
    let value: any = theme.palette;

    for (const key of path) {
      value = value?.[key];
    }

    return value || colors.primary.main;
  },

  // 获取主题间距
  spacing: (multiplier: number) => (theme: Theme) => theme.spacing(multiplier),

  // 获取主题圆角
  borderRadius: (size: keyof typeof borderRadius) => (theme: Theme) =>
    theme.shape.borderRadius || borderRadius[size],

  // 获取主题阴影
  shadow: (level: keyof typeof shadows) => (theme: Theme) =>
    (theme.shadows as any)[level] || shadows[level],
};

// 布局工具函数
export const layoutUtils = {
  // Flex布局
  flex: (
    direction: "row" | "column" = "row",
    justify: string = "flex-start",
    align: string = "stretch"
  ) => ({
    display: "flex",
    flexDirection: direction,
    justifyContent: justify,
    alignItems: align,
  }),

  // 网格布局
  grid: (columns: string = "1fr", gap: number = spacing.md) => ({
    display: "grid",
    gridTemplateColumns: columns,
    gap: themeUtils.spacing(gap),
  }),

  // 居中布局
  center: (type: "both" | "horizontal" | "vertical" = "both") => {
    const styles: SxProps<Theme> = { display: "flex" };

    switch (type) {
      case "both":
        return { ...styles, justifyContent: "center", alignItems: "center" };
      case "horizontal":
        return { ...styles, justifyContent: "center" };
      case "vertical":
        return { ...styles, alignItems: "center" };
      default:
        return styles;
    }
  },

  // 间距工具
  spacing: (vertical: number = 0, horizontal: number = vertical) => ({
    margin: themeUtils.spacing(vertical),
    padding: themeUtils.spacing(horizontal),
  }),
};

// 导出所有工具函数
export default {
  composeStyles,
  conditionalStyle,
  responsive,
  themeUtils,
  layoutUtils,
};
